import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { Package, Download, Upload } from "lucide-react";
import { Button, FilterSection } from "../../../components/ui";
import { downloadBlumTemplate } from "../../../utils/BlumPurchasesTemplate";
import { BlumPurchasesBulkUpload } from "./BlumPurchasesBulkUpload";
import { PurchasesTable } from "./PurchasesTable";
import api from "../../../services/api";

const isDateInRange = (dateInput, localFilters) => {
  if (!dateInput) return true;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return true;
  const dS = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (localFilters.fromDate) {
    const f = new Date(localFilters.fromDate);
    if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) return false;
  }
  if (localFilters.toDate) {
    const t = new Date(localFilters.toDate);
    if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) return false;
  }
  if (localFilters.date && localFilters.date !== 'all') {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (localFilters.date === 'today') return dS === today;
    if (localFilters.date === 'this_week') { const s = new Date(today); s.setDate(s.getDate() - 6); return dS >= s.getTime(); }
    if (localFilters.date === 'this_month') { const s = new Date(today); s.setDate(s.getDate() - 30); return dS >= s.getTime(); }
    if (localFilters.date === 'this_year') { const s = new Date(today); s.setDate(s.getDate() - 364); return dS >= s.getTime(); }
  }
  return true;
};

export const PurchasesView = ({
  search,
  setSearch,
  filters,
  setFilters,
  dynamicSettings,
  activeTab,
  activeInventory,
  inventory,
  purchases = [],
  setInventorySubTab,
  handleNewPurchase,
  setViewItem,
  setRestockItem,
  setInitialReturnInvoice,
  setSelPR,
  setPurchaseReturnOpen,
  setEditingItem,
  setForm,
  setAddOpen,
  setPurchaseOpen,
  refreshAll
}) => {
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleFilterChange = (key, val) =>
    setFilters(prev => ({ ...prev, [key]: val }));

  const hasOpenStockMatches = search && (inventory || []).some(i =>
    ((i.name || '').toLowerCase().includes(search.toLowerCase()) || (i.itemName || '').toLowerCase().includes(search.toLowerCase())) &&
    (i.invoiceNo === 'OPEN-STOCK' || i.supplier === 'Opening Stock')
  );

  React.useEffect(() => {}, []);

  const handleToggleStatus = async (item) => {
    try {
      const newStatus = item.status === 'Inactive' ? 'Active' : 'Inactive';
      await api.put(`/v1/purchase-master/${item._id}`, { status: newStatus });
      toast.success(`Item status updated to ${newStatus}`);
      // Triggers parent refresh
      if (refreshAll) refreshAll();
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update item status");
    }
  };


  if (purchases.length === 0 && hasOpenStockMatches) {
    return (
      <div className="card p-12 flex flex-col items-center justify-center bg-white shadow-sm border border-slate-100 rounded-2xl animate-in zoom-in-95 duration-500">
        <div className="w-16 h-16 bg-blue-50 text-brand-blue rounded-full flex items-center justify-center mb-4">
          <Package size={32} />
        </div>
        <h2 className="text-xl font-bold text-brand-dark mb-1">Found in Closing Stock</h2>
        <p className="text-slate-500 text-sm mb-6 text-center max-w-md">
          No purchase records found for "{search}". However, this item has entries in the Closing Stock records.
        </p>
        <button 
          onClick={() => setInventorySubTab("open_stock")}
          className="btn-primary px-8 py-2.5 text-xs font-black uppercase tracking-widest flex items-center gap-2"
        >
          View Closing Stock Records
        </button>
      </div>
    );
  }

  // nithin: Re-added Export logic for Purchases
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      //updated Halimunnisa 2026-06-08
      //Implemented a comprehensive export functionality for purchases that allows users to easily export their purchase data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their purchase information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting purchase records.
      const exportPurchases = purchases || [];

      if (exportPurchases.length === 0) {
        toast.error("No purchases to export");
        return;
      }

      const { exportToExcel } = await import("../../../utils/exportUtils");
      
      const exportData = exportPurchases.map(p => {
        const resolveName = (value, type) => {
          if (!value) return '-';
          if (typeof value === 'object') return value.storeName || value.vendorName || value.title || value.name || value.label || value.categoryName || '-';
          return String(value);
        };
        
        const vendor = p.vendorName || resolveName(p.vendor || p.supplier, 'vendor');
        const itm = p.item || p;
        const itemName = itm.itemName || itm.name || '-';
        
        const getVal = (obj, fields) => {
          if (!obj) return null;
          if (typeof obj === 'string') return obj;
          if (typeof obj === 'object') {
            for (const f of fields) {
              if (obj[f] && typeof obj[f] === 'string') return obj[f];
            }
          }
          return null;
        };
        const safeUnit = getVal(itm.units, ['title', 'name', 'unitName', 'label']) || 
                         getVal(itm.unit, ['title', 'name', 'unitName', 'label']) || 
                         p.uom || '-';
                // Determine quantity to export, prioritizing availableQty, then stock, then quantity         
                //updated Halimunnisa 2026-06-08
        const qty = p.quantity !== undefined ? p.quantity : (p.availableQty !== undefined ? p.availableQty : (p.stock !== undefined ? p.stock : '-'));

        const catResolved = resolveName(p.category, 'category');
        const subCatResolved = resolveName(p.subCategory, 'subCategory');
        const categoryName = p.categoryName || catResolved;
        const subCategoryName = p.subCategoryName || subCatResolved;

        return {
          'Date': p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString() : p.date ? new Date(p.date).toLocaleDateString() : p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-',
          'Invoice Number': p.version || p.invoiceNumber || p.invoiceNo || p.billNo || '-',
          'Vendor/Supplier': vendor,
          'Category': categoryName || '-',
          'Sub Category': subCategoryName || '-',
          'Article ID': p.articleId || '-',
          'Description': p.description || '-',
          'Information': p.information || '-',
          'Internal Type': p.internalType || '-',
          'Shutter Type': p.shutterType || '-',
          'Width': p.width || '-',
          'Height': p.height || '-',
          'Depth': p.depth || '-',
          'UOM': p.uom || resolveName(p.unit || p.units, 'uom') || '-',
          'MOQ': p.moq || '-',
          'Purchased Quantity': p.quantity !== undefined ? p.quantity : '-',
          'Available Quantity': qty,
          'Purchase Amount': p.blumPrice || '-',
          'Verve Price': p.vervePrice || '-',
          'GST %': p.gstPercentage || '-',
          'Status': p.status || 'Active',
          'Remarks': p.remark || p.remarks || '-'
        };
      });

      await exportToExcel(exportData, `Purchases_Export_${new Date().toISOString().split('T')[0]}`);
      toast.success("Purchases exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export purchases");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <FilterSection
        search={search}
        setSearch={setSearch}
        filters={filters}
        setFilters={(key, val) => setFilters(prev => ({ ...prev, [key]: val }))}
        onAddClick={handleNewPurchase}
        addLabel="Purchase"
        statusOptions={[]}
        onExport={handleExport}
        isExporting={isExporting}
        customActions={
          <>
            <Button
              onClick={() => downloadBlumTemplate()}
              variant="secondary"
              size="sm"
              className="h-9 hover:scale-105 transition-all rounded-md whitespace-nowrap !font-normal text-slate-700"
              leftIcon={<Download size={18} />}
              title="Template"
            >
              <span className="hidden lg:inline">Template</span>
            </Button>
            <label className="btn-secondary flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer px-3 h-9 text-[13px] font-normal hover:bg-slate-100 hover:scale-105 transition-all border border-slate-200 rounded-md shadow-sm text-slate-700" title="Upload">
              <input
                type="file"
                className="hidden"
                accept=".csv, .xlsx, .xls"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setBulkUploadFile(e.target.files[0]);
                    setIsBulkUploadOpen(true);
                    e.target.value = null;
                  }
                }}
              />
              <Upload size={18} /> <span className="hidden lg:inline">Upload</span>
            </label>
          </>
        }
      />

      {purchases.length > 0 && hasOpenStockMatches && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-500">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-200">
              <Package size={16} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-blue-900">Closing Stock Also Available</div>
              <p className="text-[11px] text-blue-700">Records for "{search}" also exist in the Closing Stock tab.</p>
            </div>
          </div>
          <button 
            onClick={() => setInventorySubTab("open_stock")}
            className="text-xs font-bold text-brand-blue hover:text-blue-800 bg-white px-4 py-2 rounded-lg border border-blue-100 shadow-sm transition-all active:scale-95"
          >
            Switch to Closing Stock
          </button>
        </div>
      )}
      {/* <div className="flex justify-end text-[10px] text-slate-500 font-medium pb-2 px-1 gap-2 uppercase tracking-wide animate-in fade-in">
        <span>UoM : *Unit of Measure</span>
        <span className="text-slate-300">|</span>
        <span>MOQ : *Minimum Order Quantity</span>
        <span className="text-slate-300">|</span>
        <span>MRP : *Maximum Retail Price</span>
      </div> */}
      <PurchasesTable
        purchases={purchases}
        onView={setViewItem}
        onRestock={setRestockItem}
        onReturn={(item) => {
          setInitialReturnInvoice(item.invoiceNumber || item.invoiceNo || item.billNo || "");
          setSelPR(null);
          setPurchaseReturnOpen(true);
        }}
        onEdit={(item) => {
          setEditingItem(item);
          setForm({ ...item });
          setAddOpen(false);
          setPurchaseOpen(true);
        }}
        onToggleStatus={handleToggleStatus}
        dynamicSettings={dynamicSettings}
      />

      {/* nithin: Re-using the existing BlumPurchasesBulkUpload modal for purchases */}
      {isBulkUploadOpen && (
        <BlumPurchasesBulkUpload
          isOpen={isBulkUploadOpen}
          onClose={() => {
            setIsBulkUploadOpen(false);
            setBulkUploadFile(null);
          }}
          initialFile={bulkUploadFile}
          onSuccess={() => {
             // nithin: reload master data instead of page reload
             if (refreshAll) refreshAll();
             setIsBulkUploadOpen(false);
          }}
        />
      )}

      {/* {isManageVersionsOpen && (
        <ManageVersionsModal
          isOpen={isManageVersionsOpen}
          onClose={() => setIsManageVersionsOpen(false)}
          purchases={filteredPurchases}
          onSuccess={fetchMasterData}
        />
      )} */}
    </div>
  );
};

